import { apiClient } from './client';
import type { CrmConversation, CrmInboxCounts, CrmMailbox, CrmMailboxSettings, CrmMessage, CrmStats } from '../types';

// ─── CRM mailboxes (Company Settings → CRM) ─────────────────

export const crmMailboxesApi = {
  list: async (): Promise<CrmMailbox[]> => {
    const { data } = await apiClient.get('/crm/mailboxes');
    return data.data.mailboxes;
  },
  create: async (body: { address: string; name: string; isActive?: boolean; settings?: Partial<CrmMailboxSettings> }): Promise<CrmMailbox> => {
    const { data } = await apiClient.post('/crm/mailboxes', body);
    return data.data.mailbox;
  },
  update: async (id: string, body: { address?: string; name?: string; isActive?: boolean; settings?: Partial<CrmMailboxSettings> }): Promise<CrmMailbox> => {
    const { data } = await apiClient.put(`/crm/mailboxes/${id}`, body);
    return data.data.mailbox;
  },
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/crm/mailboxes/${id}`);
  },
};

// ─── Conversations (the Inbox) ──────────────────────────────

export interface ConversationListResult {
  conversations: CrmConversation[];
  total: number;
  page: number;
  limit: number;
  counts: CrmInboxCounts;
}

export const crmConversationsApi = {
  list: async (params?: Record<string, string | number>): Promise<ConversationListResult> => {
    const { data } = await apiClient.get('/crm/conversations', { params });
    return data.data;
  },
  get: async (id: string): Promise<{ conversation: CrmConversation; messages: CrmMessage[] }> => {
    const { data } = await apiClient.get(`/crm/conversations/${id}`);
    return data.data;
  },
  /** Start a thread by email. `to`/`cc` accept comma-separated text. */
  create: async (body: { mailboxId: string; to: string; cc?: string; subject: string; body: string; contactId?: string; organizationId?: string; dealId?: string }): Promise<CrmConversation> => {
    const { data } = await apiClient.post('/crm/conversations', body);
    return data.data.conversation;
  },
  reply: async (id: string, body: { body: string; to?: string; cc?: string; closeAfter?: boolean }): Promise<CrmMessage> => {
    const { data } = await apiClient.post(`/crm/conversations/${id}/messages`, { kind: 'reply', ...body });
    return data.data.message;
  },
  note: async (id: string, body: string): Promise<CrmMessage> => {
    const { data } = await apiClient.post(`/crm/conversations/${id}/messages`, { kind: 'note', body });
    return data.data.message;
  },
  forward: async (id: string, body: { to: string; note?: string }): Promise<void> => {
    await apiClient.post(`/crm/conversations/${id}/forward`, body);
  },
  update: async (id: string, body: Partial<Pick<CrmConversation, 'status' | 'ownerId' | 'contactId' | 'organizationId' | 'dealId' | 'tags' | 'subject'>> & { blockSender?: boolean }): Promise<CrmConversation> => {
    const { data } = await apiClient.patch(`/crm/conversations/${id}`, body);
    return data.data.conversation;
  },
  createContact: async (id: string, body: { firstName?: string; lastName?: string; organizationId?: string } = {}): Promise<CrmConversation> => {
    const { data } = await apiClient.post(`/crm/conversations/${id}/contact`, body);
    return data.data.conversation;
  },
  createOrganization: async (id: string, body: { name?: string; domain?: string } = {}): Promise<CrmConversation> => {
    const { data } = await apiClient.post(`/crm/conversations/${id}/organization`, body);
    return data.data.conversation;
  },
  createDeal: async (id: string, body: { title?: string; stageId?: string; value?: number | null; expectedCloseDate?: string } = {}): Promise<CrmConversation> => {
    const { data } = await apiClient.post(`/crm/conversations/${id}/deal`, body);
    return data.data.conversation;
  },
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/crm/conversations/${id}`);
  },
};

// ─── Dashboard statistics (server-computed) ──────────────────

export const crmStatsApi = {
  get: async (days = 30): Promise<CrmStats> => {
    const { data } = await apiClient.get('/crm/stats', { params: { days } });
    return data.data;
  },
};
